import React, { useState, useEffect } from 'react';
import { useForm } from '@formspree/react';
import { Helmet } from 'react-helmet';
import { CheckCircle, XCircle, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import OracleStore from '../assets/images/OracleGaming.webp';

const Contact = () => {
  // Use environment variable with just the form ID (not full URL)
  const formspreeId = process.env.REACT_APP_FORMSPREE_ID || "xwprjpvl";
  const [state, handleSubmit] = useForm(formspreeId);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('success'); // 'success' or 'error'

  // Show modal when form state changes
  useEffect(() => {
    try {
      if (state && state.succeeded) {
        setModalType('success');
        setShowModal(true);
      } else if (state && state.errors && Array.isArray(state.errors) && state.errors.length > 0) {
        setModalType('error');
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error in useEffect:', error);
    }
  }, [state]);

  const closeModal = () => {
    setShowModal(false);
  };

  // Modal component
  const Modal = () => {
    if (!showModal) return null;

    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}
        onClick={closeModal}
      >
        <div 
          style={{
            backgroundColor: '#1a1b20',
            borderRadius: '12px',
            padding: '40px',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            border: '2px solid #14fefb',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={closeModal}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'none',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '24px'
            }}
          >
            <X size={24} />
          </button>

          {modalType === 'success' ? (
            <>
              <div style={{ marginBottom: '20px' }}>
                <CheckCircle size={64} color="#4ade80" />
              </div>
              <h2 style={{ color: '#4ade80', marginBottom: '15px', fontSize: '28px', fontWeight: 'bold' }}>
                Message Sent Successfully!
              </h2>
              <p style={{ color: '#fff', marginBottom: '25px', fontSize: '16px', lineHeight: '1.5' }}>
                Thank you for your message! We'll get back to you as soon as possible.
              </p>
              <button
                onClick={closeModal}
                className="btn btn-primary"
                style={{ padding: '12px 24px', fontSize: '16px' }}
              >
                Close
              </button>
            </>
          ) : (
            <>
              <div style={{ marginBottom: '20px' }}>
                <XCircle size={64} color="#ef4444" />
              </div>
              <h2 style={{ color: '#ef4444', marginBottom: '15px', fontSize: '28px', fontWeight: 'bold' }}>
                Message Failed to Send
              </h2>
              <p style={{ color: '#fff', marginBottom: '25px', fontSize: '16px', lineHeight: '1.5' }}>
                Sorry, there was an error sending your message. Please try again or contact us directly.
              </p>
              <button
                onClick={closeModal}
                className="btn btn-primary"
                style={{ padding: '12px 24px', fontSize: '16px' }}
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - Oracle Gaming Pokemon & One Piece TCG Store South Africa</title>
        <meta name="description" content="Get in touch with Oracle Gaming for Pokemon and One Piece card inquiries. Located in Bloemfontein, South Africa. Customer support for TCG products and tournaments." />
        <meta name="keywords" content="contact Oracle Gaming, Pokemon cards support, One Piece cards support, TCG customer service South Africa, Bloemfontein gaming store, TCG tournaments contact" />
        <meta property="og:title" content="Contact Us - Oracle Gaming Pokemon & One Piece TCG Store South Africa" />
        <meta property="og:description" content="Get in touch with Oracle Gaming for Pokemon and One Piece card inquiries. Located in Bloemfontein, South Africa. Customer support for TCG products and tournaments." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://oracle-gaming.vercel.app/contact" />
        <meta property="og:image" content="https://oracle-gaming.vercel.app/images/logo.svg" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contact Us - Oracle Gaming Pokemon & One Piece TCG Store South Africa" />
        <meta name="twitter:description" content="Get in touch with Oracle Gaming for Pokemon and One Piece card inquiries. Located in Bloemfontein, South Africa. Customer support for TCG products and tournaments." />
        <meta name="twitter:image" content="https://oracle-gaming.vercel.app/images/logo.svg" />
        <link rel="canonical" href="https://oracle-gaming.vercel.app/contact" />
      </Helmet>
      <Header />
      <main style={{ 
        backgroundColor: '#1a1b20', 
        minHeight: '100vh',
        margin: 0,
        padding: 0
      }}>
        <article 
          className="page-offset" 
          style={{ 
            backgroundColor: '#1a1b20',
            margin: 0,
            padding: 0
          }}
        >
          <div 
            className="section-wrapper" 
            style={{ 
              backgroundColor: '#1a1b20',
              margin: 0,
              padding: 0
            }}
          >
            <section 
              className="contact" 
              id="contact" 
              style={{ 
                backgroundColor: '#1a1b20', 
                paddingTop: '180px',
                margin: 0
              }}
            >
              <div className="container" style={{ backgroundColor: '#1a1b20' }}>
                <style>{`
                  @media (max-width: 768px) {
                    .page-offset,
                    .section-wrapper,
                    .container {
                      background-color: #1a1b20 !important;
                      margin: 0 !important;
                      padding-left: 16px !important;
                      padding-right: 16px !important;
                    }

                    /* Mobile-specific: Add more top padding to prevent header overlap */
                    .contact {
                      background-color: #1a1b20 !important;
                      margin: 0 !important;
                      padding-top: 220px !important; /* More space for mobile header */
                      padding-left: 0 !important;
                      padding-right: 0 !important;
                    }

                    /* Keep desktop spacing intact for intro paragraph */
                    .about-oracle-description {
                      margin-top: -40px !important; /* Keep original desktop spacing */
                      margin-bottom: 30px !important;
                    }

                    main {
                      background-color: #1a1b20 !important;
                      margin: 0 !important;
                      padding: 0 !important;
                    }
                  }
                `}</style>
                <div>
                  <h2 className="h2 section-title">Get in touch</h2>
                  <p className="about-oracle-description" style={{ textAlign: 'center', marginTop: '-40px', marginBottom: '30px' }}>
                    We'd love to hear from you. Send us a message and we'll reply as soon as possible.
                  </p>

                  <form className="contact-form" onSubmit={handleSubmit}>
                    <div className="form-row">
                      <div className="form-field">
                        <label htmlFor="name">Name</label>
                        <input id="name" name="name" type="text" placeholder="Your name" required />
                        {state.errors && state.errors.length > 0 && (
                          <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>
                            Please check your name
                          </div>
                        )}
                      </div>
                      <div className="form-field">
                        <label htmlFor="email">Email</label>
                        <input id="email" name="email" type="email" placeholder="you@example.com" required />
                        {state.errors && state.errors.length > 0 && (
                          <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>
                            Please check your email
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-field">
                      <label htmlFor="subject">Subject</label>
                      <input id="subject" name="subject" type="text" placeholder="How can we help?" required />
                      {state.errors && state.errors.length > 0 && (
                        <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>
                          Please check your subject
                        </div>
                      )}
                    </div>
                    <div className="form-field">
                      <label htmlFor="message">Message</label>
                      <textarea id="message" name="message" rows="5" placeholder="Write your message here..." required></textarea>
                      {state.errors && state.errors.length > 0 && (
                        <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>
                          Please check your message
                        </div>
                      )}
                    </div>
                    <button type="submit" disabled={state.submitting || false} className="btn btn-primary">
                      {state.submitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>

                  <div className="about-oracle-details" style={{ marginTop: '30px' }}>
                    <div className="detail-item">
                      <ion-icon name="location-outline"></ion-icon>
                      <div>
                        <h4>Address</h4>
                        <p>11a President Steyn Ave, Westdene, Bloemfontein, 9301</p>
                      </div>
                    </div>
                    <div className="detail-item">
                      <ion-icon name="call-outline"></ion-icon>
                      <div>
                        <h4>Phone</h4>
                        <p>082 565 5408</p>
                      </div>
                    </div>
                    <div className="detail-item">
                      <ion-icon name="mail-outline"></ion-icon>
                      <div>
                        <h4>Email</h4>
                        <p>oraclegaming.za@gmail.com</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="about-oracle-visual">
                  <div className="about-oracle-image">
                    <img src={OracleStore} alt="Oracle Gaming storefront" />
                  </div>
                  <div className="about-oracle-map">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3338.123456789!2d26.123456!3d-29.123456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjnCsDA3JzI0LjQiUyAyNsKwMDcnMjQuNCJF!5e0!3m2!1sen!2sza!4v1234567890123!5m2!1sen!2sza&style=feature:all|element:labels|visibility:off&style=feature:road|element:geometry|color:0x2d2d2d&style=feature:water|element:geometry|color:0x1a1a1a&style=feature:landscape|element:geometry|color:0x2d2d2d"
                      width="100%"
                      height="260"
                      style={{ border: 0, borderRadius: '8px' }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Location"
                    ></iframe>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </article>
      </main>
      <Footer />
      <Modal />
    </>
  );
};

export default Contact;
